import { supabase } from "./supabaseClient";

// Client-side role resolution — used ONLY for redirect/UX decisions
// (which portal to send someone to, which brand mark to show). It is
// NOT a security boundary: the worker re-resolves role from table
// membership on every request regardless of what the client believes
// (see worker/src/lib/roles.ts, the actual enforcement point). A
// stale or spoofed client-side read here can, at worst, send someone
// to the wrong screen — it can never grant data access, because the
// backend checks independently.
//
// Mirrors the worker's collision rule exactly: a user present in both
// `users` and `volunteer_accounts` never auto-resolves to "admin".

export type ResolvedRole = "admin" | "volunteer" | "none";

export async function resolveClientRole(userId: string): Promise<ResolvedRole> {
  if (!supabase) return "none";
  const [adminRes, volunteerRes] = await Promise.all([
    supabase.from("users").select("id").eq("id", userId),
    supabase.from("volunteer_accounts").select("id").eq("id", userId),
  ]);
  const isAdmin = (adminRes.data?.length ?? 0) > 0;
  const isVolunteer = (volunteerRes.data?.length ?? 0) > 0;
  if (isAdmin && isVolunteer) return "volunteer";
  if (isAdmin) return "admin";
  if (isVolunteer) return "volunteer";
  return "none";
}

interface VolunteerParishRow {
  parishes: { slug: string } | null;
}

// The parish slug a volunteer's own account belongs to — needed to
// build a /:parish/dashboard redirect target from an unscoped route
// (root, /sign-in) that has no slug of its own. Requires the
// parishes_volunteer_select RLS policy (migration 0007); returns null
// on any failure so callers can fall back gracefully rather than
// throwing mid-redirect.
export async function resolveVolunteerParishSlug(userId: string): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("volunteer_accounts")
    .select("parishes(slug)")
    .eq("id", userId)
    .maybeSingle<VolunteerParishRow>();
  if (error || !data?.parishes?.slug) return null;
  return data.parishes.slug;
}
